import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, MessageSquare, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { showNoteDialog } from './showNoteDialog';
import type { CCompareItem } from '@arrowise/shared';

interface CompareListItemProps {
    item: CCompareItem;
    positionId?: string;
}

export default function CompareListItem({ item, positionId }: CompareListItemProps) {
    const navigate = useNavigate();

    const candidateName = item.candidateName || 'N/A';
    const candidateEmail = item.candidateEmail || 'N/A';

    // Check if note is longer than what fits in 2 lines (roughly 50 characters for safety)
    const isNoteLong = item.latestOverallNote && item.latestOverallNote.length > 50;

    return (
        <Card className="h-82 flex flex-col">
            <CardContent className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-muted-foreground">Name:</span>
                                <span className="text-sm text-foreground truncate">{candidateName}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-muted-foreground">Email:</span>
                                <span className="text-sm text-foreground truncate">{candidateEmail}</span>
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                                {item.graded ? (
                                    <Badge
                                        variant="default"
                                        className="text-xs px-2 py-1 bg-chart-1/10 text-chart-1 border-chart-1/20"
                                    >
                                        Assessment complete
                                    </Badge>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs px-2 py-1 bg-muted text-muted-foreground border border-border h-6"
                                        onClick={() => navigate(`/app/sessions/${item.sessionId}/view?tab=assessments&from=compare&positionId=${positionId}`)}
                                    >
                                        Assessment incomplete
                                        <ArrowRight className="h-3 w-3 ml-1" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/sessions/${item.sessionId}/view?from=compare&positionId=${positionId}`)}
                        >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                        </Button>
                    </div>
                </div>

                <div className="mb-4">
                    <span className="text-sm font-medium text-muted-foreground">
                        Average Score: {item.averageScore ? `${item.averageScore.toFixed(1)}/10` : 'No score yet'}
                    </span>
                </div>

                <div className="mt-auto h-32 pb-4">
                    <div className="bg-muted/30 rounded-lg p-4 border border-border/30 h-full flex flex-col max-w-full overflow-hidden">
                        {item.latestOverallNote ? (
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                <MessageSquare className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium text-muted-foreground">Summary Note</span>
                                        {isNoteLong && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => showNoteDialog(item.latestOverallNote!, candidateName)}
                                                className="h-6 px-2 text-xs hover:bg-accent/50 opacity-100 hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0"
                                            >
                                                Full Note
                                                <ArrowRight className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 whitespace-pre-wrap break-words max-w-full overflow-hidden">
                                        {item.latestOverallNote}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <span className="text-sm text-muted-foreground">No notes yet</span>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
